import { Box, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CatalogItemSource } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { CatalogItemCheckbox } from 'features/CatalogItemCheckbox';
import { DynamicTooltip } from 'shared/ui/Tooltip/ui/DynamicTooltip';

export interface CatalogItemCheckboxSectionProps {
    showQtyCheckbox: boolean;
    isActive: boolean;
    qty: number;
    itemIdToEdit: string | undefined;
    itemId: string;
    originalItemId: string;
    itemUid: string | undefined;
    quickEdit: boolean | undefined;
    isInOrder: boolean | undefined;
    showActionRequiredTooltip: boolean;
    onLearnMoreActionRequired: () => void;
    isAvailable?: boolean;
    source?: CatalogItemSource;
}

interface ActionRequiredTooltipContentProps {
    onLearnMore: () => void;
}

const ActionRequiredTooltipContent = ({
    onLearnMore,
}: ActionRequiredTooltipContentProps) => {
    const { t } = useTranslation();
    return (
        <Box>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 600,
                    marginBottom: '8px',
                }}
            >
                {t('Action Required')}
            </Typography>
            <Typography typography="openSans.caption" sx={{ mb: '8px' }}>
                {t(
                    // eslint-disable-next-line max-len
                    'To add this item to your property catalog, you need an active account with one of the suppliers below.',
                )}
            </Typography>
            <Link
                component="button"
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onLearnMore();
                }}
                sx={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '12px',
                }}
            >
                {t('Learn more')}
            </Link>
        </Box>
    );
};

export const CatalogItemCheckboxSection = ({
    showQtyCheckbox,
    isActive,
    qty,
    itemIdToEdit,
    itemId,
    originalItemId,
    itemUid,
    quickEdit,
    isInOrder,
    showActionRequiredTooltip,
    onLearnMoreActionRequired,
    isAvailable = true,
    source,
}: CatalogItemCheckboxSectionProps) => {
    if (showActionRequiredTooltip) {
        return (
            <DynamicTooltip
                maxWidth={400}
                content={(closeTooltip) => (
                    <ActionRequiredTooltipContent
                        onLearnMore={() => {
                            onLearnMoreActionRequired();
                            closeTooltip();
                        }}
                    />
                )}
            >
                <CatalogItemCheckbox
                    showQtyCheckbox={showQtyCheckbox}
                    isActive={false}
                    qty={qty}
                    itemIdToEdit={itemIdToEdit}
                    itemId={itemId}
                    originalItemId={originalItemId}
                    itemUid={itemUid}
                    quickEdit={quickEdit}
                    isInOrder={isInOrder}
                    disabled
                    isAvailable={isAvailable}
                    source={source}
                />
            </DynamicTooltip>
        );
    }

    return (
        <CatalogItemCheckbox
            showQtyCheckbox={showQtyCheckbox}
            isActive={isActive}
            qty={qty}
            itemIdToEdit={itemIdToEdit}
            itemId={itemId}
            originalItemId={originalItemId}
            itemUid={itemUid}
            quickEdit={quickEdit}
            isInOrder={isInOrder}
            isAvailable={isAvailable}
            source={source}
        />
    );
};
